import { Link } from "react-router-dom";
import { FileText, Shield, Mail } from "lucide-react";

const SECTIONS = [
  {
    title: "1. Acceptance of these terms",
    body: [
      "These Terms of Use (\"Terms\") govern your access to and use of Phoenix Dance Academy's website (phoenixdanceacademy.com), studios, classes, online programs, and any services we offer (collectively, the \"Services\"). By visiting our website, enquiring about a class, attending a trial, or enrolling as a student, you agree to be bound by these Terms.",
      "If you do not agree with any part of these Terms, please do not use the Services.",
    ],
  },
  {
    title: "2. About us",
    body: [
      "Phoenix Dance Academy is an international dance academy founded in 2014 in Chennai, India, with branches in Sacramento (USA, est. 2021) and Kitchener (Canada, est. 2022). We offer dance training across multiple styles for students of all ages, including kids' programs, beginner-to-advanced training, wedding choreography, and event productions.",
    ],
  },
  {
    title: "3. Eligibility & student conduct",
    body: [
      "By enrolling, you confirm that:",
    ],
    list: [
      "You are at least 18 years old, or you are enrolling as a parent/legal guardian on behalf of a minor.",
      "All information you provide is accurate, complete, and current.",
      "You will follow the studio's code of conduct — respect instructors and fellow students, attend classes on time, and refrain from disruptive or harmful behaviour.",
      "You will not record, photograph, or live-stream class sessions or choreography without the academy's prior written permission.",
    ],
  },
  {
    title: "4. Enrollment, trials & free classes",
    body: [
      "We offer trial classes to help you experience Phoenix before enrolling. Trial slots are subject to availability and at the discretion of the branch. After the trial, enrollment is confirmed only once full registration is completed and applicable fees are paid.",
      "We reserve the right to refuse or cancel an enrollment if it is not in line with our values, safety standards, or batch composition.",
    ],
  },
  {
    title: "5. Fees, payments & refunds",
    body: [
      "Class fees vary by branch, program, age group, and intensity. The fee for your chosen program will be communicated to you before enrollment. Unless explicitly stated otherwise:",
    ],
    list: [
      "Fees are billed monthly or per term, in advance.",
      "Fees, once paid, are non-refundable except where required by law or specifically agreed in writing.",
      "Missed classes due to student absence are not refunded but may be eligible for a make-up session at the branch's discretion.",
      "Phoenix may revise fees at any time with at least 30 days' notice to currently enrolled students.",
    ],
  },
  {
    title: "6. Cancellations & rescheduling",
    body: [
      "Phoenix reserves the right to cancel, reschedule, or substitute instructors for any class due to weather, holidays, instructor availability, low enrollment, or unforeseen circumstances. We will make reasonable efforts to notify you in advance and to schedule a make-up class where possible.",
    ],
  },
  {
    title: "7. Photography, video & media release",
    body: [
      "We frequently capture photos, videos, and live recordings of classes, performances, rehearsals, and events for promotional, social-media, and educational purposes. By enrolling, you (or your parent/guardian, if you are a minor) consent to Phoenix's use of such media across our website, social channels, marketing collateral, and partner platforms — without compensation.",
      "If you do not wish to be featured, please notify us in writing at phoenixacademy2506@gmail.com. We will make reasonable efforts to honour your preference going forward.",
    ],
  },
  {
    title: "8. Intellectual property",
    body: [
      "All choreography, training material, syllabus, designs, branding, photographs, videos, and content on the website are the intellectual property of Phoenix Dance Academy or its licensors and are protected by copyright and trademark laws.",
      "You may not copy, reproduce, modify, distribute, perform publicly, or commercially use any of our content, choreography, or branding without our prior written permission.",
    ],
  },
  {
    title: "9. Health, fitness & assumption of risk",
    body: [
      "Dance training is a physical activity that involves inherent risks of injury. By participating, you acknowledge that:",
    ],
    list: [
      "You are physically fit to participate, or have consulted a qualified medical professional.",
      "You will inform your instructor of any pre-existing condition, injury, allergy, or medication that may affect your training.",
      "You voluntarily assume the risk of injury and waive any claim against Phoenix Dance Academy, its instructors, branch operators, and staff for injuries arising from your participation, except where caused by gross negligence.",
    ],
  },
  {
    title: "10. Online classes & digital content",
    body: [
      "When you participate in online classes or access digital training material, you agree not to share, redistribute, copy, screen-record, or upload that content to any public platform. Login credentials, links, and recorded material are for your personal use only.",
    ],
  },
  {
    title: "11. Third-party links & integrations",
    body: [
      "Our website may contain links to or integrate with third-party services such as JotForm, WhatsApp, Instagram, YouTube, and Google Maps. Phoenix is not responsible for the content, policies, or practices of those third-party services. Your use of them is governed by their own terms and privacy policies.",
    ],
  },
  {
    title: "12. Disclaimer of warranties",
    body: [
      "The Services are provided on an \"as-is\" and \"as-available\" basis. While we work hard to keep the website and our classes high-quality and reliable, Phoenix makes no warranties — express or implied — regarding the accuracy, reliability, completeness, or availability of the Services.",
    ],
  },
  {
    title: "13. Limitation of liability",
    body: [
      "To the fullest extent permitted by law, Phoenix Dance Academy, its founders, instructors, employees, and affiliates shall not be liable for any indirect, incidental, special, consequential, or punitive damages — including loss of profits, data, or goodwill — arising from your use of the Services. Our total liability, where any liability is found, is limited to the amount you have paid Phoenix in the 12 months preceding the claim.",
    ],
  },
  {
    title: "14. Termination",
    body: [
      "Phoenix may suspend or terminate your enrollment or access to the Services at any time, without refund, if you violate these Terms or behave in a manner that is unsafe, disrespectful, or harmful to instructors, fellow students, or the academy's reputation.",
    ],
  },
  {
    title: "15. Governing law & jurisdiction",
    body: [
      "These Terms are governed by the laws of the country and state in which the relevant Phoenix branch is registered (India, the United States, or Canada, as applicable). Any dispute arising from or related to these Terms shall be subject to the exclusive jurisdiction of the courts of that location.",
    ],
  },
  {
    title: "16. Changes to these terms",
    body: [
      "We may update these Terms from time to time to reflect changes in our practices, services, or applicable laws. The latest version will always be available on this page with an updated \"Last revised\" date. Continued use of our Services after a change constitutes your acceptance of the revised Terms.",
    ],
  },
  {
    title: "17. Contact",
    body: [
      "For questions about these Terms or any of our Services, please reach out:",
      "Phoenix Dance Academy",
      "Main Branch — No.267, MKB Nagar, Vyasarpadi, Chennai 600039, India",
      "Email: phoenixacademy2506@gmail.com",
      "Phone / WhatsApp: +91 82482 14953",
    ],
  },
];

export default function Terms() {
  return (
    <div data-testid="terms-page">
      {/* Hero */}
      <section className="relative overflow-hidden border-b border-white/10">
        <div className="absolute inset-0 bg-gradient-to-br from-[#1a0a05] via-black to-black" />
        <div className="relative max-w-[1400px] mx-auto px-6 lg:px-10 pt-32 lg:pt-40 pb-20">
          <div className="flex items-center gap-3 mb-6">
            <FileText className="w-4 h-4 text-[#ff4500]" />
            <p className="text-xs uppercase tracking-[0.3em] text-[#ffb700]">◆ Legal</p>
          </div>
          <h1 className="font-display text-5xl lg:text-7xl text-white leading-[0.95]">
            Terms <span className="phoenix-text">of Use.</span>
          </h1>
          <p className="mt-6 text-base lg:text-lg text-white/65 max-w-2xl leading-relaxed">
            The rules, responsibilities, and protections that apply when you use Phoenix Dance Academy's website, studios, and online programs.
          </p>
          <p className="mt-8 text-xs uppercase tracking-[0.25em] text-white/40">
            Last revised · February 2026
          </p>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 lg:py-28">
        <div className="max-w-[1100px] mx-auto px-6 lg:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Sticky TOC */}
          <aside className="lg:col-span-3 hidden lg:block">
            <div className="sticky top-28">
              <p className="text-xs uppercase tracking-[0.25em] text-[#ffb700] mb-4">◆ On this page</p>
              <ul className="space-y-2 text-xs text-white/55">
                {SECTIONS.map((s, i) => (
                  <li key={i}>
                    <a href={`#sec-${i}`} className="hover:text-[#ff4500] transition-colors leading-relaxed block">
                      {s.title}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <article className="lg:col-span-9 space-y-12">
            {SECTIONS.map((s, i) => (
              <section key={i} id={`sec-${i}`} data-testid={`terms-sec-${i}`}>
                <h2 className="font-display text-2xl lg:text-3xl text-white mb-5 leading-tight">
                  {s.title}
                </h2>
                {s.body?.map((p, j) => (
                  <p key={j} className="text-sm lg:text-base text-white/70 leading-relaxed mb-4">
                    {p}
                  </p>
                ))}
                {s.list && (
                  <ul className="space-y-2 mb-4 list-none">
                    {s.list.map((it, j) => (
                      <li key={j} className="text-sm lg:text-base text-white/70 leading-relaxed flex gap-3">
                        <span className="text-[#ff4500] mt-1.5 shrink-0">◆</span>
                        <span>{it}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </section>
            ))}

            {/* Bottom CTA */}
            <div className="border border-white/10 bg-[#0a0a0a] p-8 mt-16">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#ff4500]/15 border border-[#ff4500]/30 flex items-center justify-center shrink-0">
                  <Mail className="w-4 h-4 text-[#ff4500]" />
                </div>
                <div>
                  <p className="font-display text-xl text-white mb-2">Questions about these Terms?</p>
                  <p className="text-sm text-white/65 leading-relaxed mb-4">
                    Reach our team at <a href="mailto:phoenixacademy2506@gmail.com" className="text-[#ffb700] hover:text-[#ff4500] transition-colors">phoenixacademy2506@gmail.com</a> — we respond within 24 hours on weekdays.
                  </p>
                  <div className="flex flex-wrap gap-3 text-xs">
                    <Link to="/contact" className="border border-white/15 text-white px-4 h-9 inline-flex items-center uppercase tracking-wider hover:bg-[#ff4500] hover:border-[#ff4500] transition-colors">
                      Contact us
                    </Link>
                    <Link to="/privacy" className="border border-white/15 text-white px-4 h-9 inline-flex items-center uppercase tracking-wider hover:bg-[#ffb700] hover:text-black hover:border-[#ffb700] transition-colors">
                      <Shield className="w-3.5 h-3.5 mr-2" /> Privacy Policy
                    </Link>
                  </div>
                </div>
              </div>
            </div>
          </article>
        </div>
      </section>
    </div>
  );
}
